import React, { useState, useEffect, useRef } from 'react';
import { Bomb, Lock, Eye, Upload, Clock, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { creatorAPI, mediaAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

function ExplodingViewer({ item, onExpired }) {
  const [viewing, setViewing] = useState(false);
  const [countdown, setCountdown] = useState(item.expire_seconds);
  const [exploded, setExploded] = useState(false);
  const timerRef = useRef(null);

  const handleView = async () => {
    try {
      const r = await creatorAPI.viewExploding(item.id);
      setViewing(true);
      // Start countdown
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setExploded(true);
            creatorAPI.expireExploding(item.id).catch(() => {});
            setTimeout(() => onExpired?.(item.id), 1500);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot view');
    }
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  if (exploded) {
    return (
      <motion.div
        initial={{ scale: 1, opacity: 1 }}
        animate={{ scale: 0, opacity: 0, rotate: 180 }}
        transition={{ duration: 0.6 }}
        className="p-3 rounded-xl bg-zinc-800 border border-red-500/30 text-center"
      >
        <Bomb size={24} className="mx-auto text-red-400 mb-1" />
        <p className="text-xs text-red-400 font-bold">POOF!</p>
      </motion.div>
    );
  }

  if (viewing && item.media_url) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative rounded-xl overflow-hidden border border-rose-500/30">
        <img src={item.media_url} alt="Exploding" className="w-full max-h-48 object-cover" />
        {item.message && <p className="px-3 py-1.5 text-xs text-zinc-300 bg-zinc-900">{item.message}</p>}
        <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/80 backdrop-blur">
          <Clock size={10} className="text-white" />
          <span className="text-white text-xs font-mono font-bold">{countdown}s</span>
        </div>
        <motion.div
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: item.expire_seconds, ease: 'linear' }}
          className="absolute bottom-0 left-0 h-0.5 bg-red-500"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-rose-500/10 flex items-center justify-center flex-shrink-0">
          {item.paid ? <Eye size={16} className="text-rose-400" /> : <Lock size={16} className="text-zinc-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-zinc-400">Exploding media</p>
          <p className="text-[10px] text-zinc-500">{item.expire_seconds}s viewing window</p>
        </div>
        <Button onClick={handleView} size="sm" className="h-7 px-3 bg-rose-500 text-white text-xs font-bold rounded-full" data-testid={`view-exploding-${item.id}`}>
          {item.price > 0 && !item.paid ? `$${item.price}` : 'View'}
        </Button>
      </div>
    </motion.div>
  );
}

export default function ExplodingMedia({ roomId, isCreator, subscriberIds }) {
  const [inbox, setInbox] = useState([]);
  const [sending, setSending] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [expireSec, setExpireSec] = useState('10');
  const [price, setPrice] = useState('0');
  const [recipientId, setRecipientId] = useState('');

  useEffect(() => {
    if (!isCreator) {
      creatorAPI.getExplodingInbox().then(r => setInbox(r.data || [])).catch(() => {});
    }
  }, [isCreator]);

  const handleSend = async () => {
    if (!file || !recipientId.trim()) { toast.error('Need file + recipient'); return; }
    setSending(true);
    try {
      const uploadRes = await mediaAPI.upload(file);
      const mediaUrl = mediaAPI.fileUrl(uploadRes.data.path);
      await creatorAPI.sendExploding({
        recipient_id: recipientId,
        media_url: mediaUrl,
        message,
        expire_seconds: parseInt(expireSec) || 10,
        price: parseFloat(price) || 0,
      });
      toast.success('Exploding media sent!');
      setShowSend(false);
      setFile(null); setMessage(''); setRecipientId('');
    } catch (e) { toast.error('Send failed'); }
    finally { setSending(false); }
  };

  const handleExpired = (id) => setInbox(prev => prev.filter(i => i.id !== id));

  return (
    <div className="border-t border-zinc-800 bg-zinc-900/50" data-testid="exploding-media">
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-xs font-bold text-rose-400 flex items-center gap-1.5">
          <Bomb size={12} /> Exploding Media
          {inbox.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold">{inbox.length}</span>}
        </span>
        {isCreator && (
          <Button onClick={() => setShowSend(!showSend)} variant="ghost" size="sm" className="text-zinc-400 text-xs h-6 px-2" data-testid="send-exploding-btn">
            {showSend ? 'Cancel' : 'Send'}
          </Button>
        )}
      </div>

      {/* Creator: send form */}
      <AnimatePresence>
        {showSend && isCreator && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3 space-y-1.5">
              <Input value={recipientId} onChange={e => setRecipientId(e.target.value)} placeholder="Recipient user ID" className="h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="exploding-recipient-input" />
              <div className="flex items-center gap-1.5">
                <label className="flex-1 flex items-center gap-2 px-2 py-1.5 rounded bg-zinc-800 border border-zinc-700 cursor-pointer hover:border-rose-500/50 transition-colors">
                  <Upload size={12} className="text-zinc-400" />
                  <span className="text-xs text-zinc-400 truncate">{file ? file.name : 'Choose file...'}</span>
                  <input type="file" accept="image/*,video/*" onChange={e => setFile(e.target.files?.[0])} className="hidden" data-testid="exploding-file-input" />
                </label>
              </div>
              <Input value={message} onChange={e => setMessage(e.target.value)} placeholder="Message (optional)" className="h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="exploding-message-input" />
              <div className="flex gap-1.5">
                <Input type="number" value={expireSec} onChange={e => setExpireSec(e.target.value)} placeholder="Seconds" className="w-16 h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="exploding-expire-input" />
                <span className="text-[10px] text-zinc-500 self-center">sec</span>
                <Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="Price ($0=free)" className="flex-1 h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="exploding-price-input" />
              </div>
              <Button onClick={handleSend} disabled={sending} size="sm" className="w-full h-7 bg-rose-500 text-white text-xs font-bold rounded" data-testid="confirm-send-exploding-btn">
                <Sparkles size={10} className="mr-1" /> {sending ? 'Sending...' : 'Send Exploding Media'}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Viewer: inbox */}
      {!isCreator && inbox.length > 0 && (
        <div className="px-3 pb-3 space-y-2">
          {inbox.map(item => (
            <ExplodingViewer key={item.id} item={item} onExpired={handleExpired} />
          ))}
        </div>
      )}
    </div>
  );
}
